import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "../contexts/ThemeContext";
import { useAnalytics } from "../hooks/useAnalytics";
import "./LandingPage.css";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_495d51fc-7735-4b73-82d1-70c12a9ac045/artifacts/bdpki6z4_AGENT-EROS-LOGO3.png";
const FOOTER_URL = "https://customer-assets.emergentagent.com/job_495d51fc-7735-4b73-82d1-70c12a9ac045/artifacts/jxk6lwf5_AGENT-EROS-FOOTER.png";

export default function VerifiedPage() {
  const { theme } = useTheme();
  const [searchParams] = useSearchParams();
  const { trackConversion } = useAnalytics();
  const status = searchParams.get("status") || "success";
  const rewardToken = searchParams.get("reward") || "";
  const verifyToken = searchParams.get("t") || "";
  const particlesRef = useRef(null);

  const isAlready = status === "already";
  const isExpired = status === "expired";

  // Reward claim state (unchanged — /verified still auto-claims the coin bonus)
  const [rewardState, setRewardState] = useState(null);
  const claimAttempted = useRef(false);

  // Registration/login link fetched from /api/waitlist/reg-link.
  // Fallback to a plain /auth?tab=register link if the token is missing
  // or the endpoint fails so the CTA always has somewhere to go.
  const API = process.env.REACT_APP_BACKEND_URL;
  const [regLinkData, setRegLinkData] = useState(null);

  useEffect(() => {
    if (isExpired) return;
    if (!verifyToken) {
      setRegLinkData({ reg_link: `${API}/auth?tab=register`, has_account: false });
      return;
    }
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/waitlist/reg-link?token=${encodeURIComponent(verifyToken)}`
        );
        if (res.ok) {
          setRegLinkData(await res.json());
        } else {
          setRegLinkData({ reg_link: `${API}/auth?tab=register`, has_account: false });
        }
      } catch (e) {
        console.error("reg-link fetch failed:", e);
        setRegLinkData({ reg_link: `${API}/auth?tab=register`, has_account: false });
      }
    })();
  }, [verifyToken, isExpired, API]);

  // Auto-claim reward token (unchanged)
  useEffect(() => {
    if (!rewardToken || claimAttempted.current) return;
    claimAttempted.current = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/mailer/verify-reward/claim`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token: rewardToken }),
        });
        if (res.ok) {
          const data = await res.json();
          setRewardState(data);
        }
      } catch (e) {
        console.error("Reward claim error:", e);
      }
    })();
  }, [rewardToken, API]);

  // Reuse the landing page particle effect
  useEffect(() => {
    const canvas = particlesRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const particles = [];
    const colors = theme === "agent_eros_1"
      ? ["#22d3ee", "#a855f7", "#ec4899"]
      : ["#C5A55A", "#e8d5a3", "#8B0000"];

    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    for (let i = 0; i < 30; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 2 + 0.5,
        dx: (Math.random() - 0.5) * 0.4,
        dy: (Math.random() - 0.5) * 0.4,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of particles) {
        p.x += p.dx; p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [theme]);

  const isCyber = theme === "agent_eros_1";
  const hasAccount = regLinkData?.has_account;
  const ctaLabel = hasAccount ? "Log In" : "Complete Registration";
  const ctaHref = regLinkData?.reg_link || `${API}/auth?tab=register`;

  const handleCtaClick = () => {
    trackConversion(
      hasAccount ? "verified_cta_login" : "verified_cta_register",
      "/verified"
    );
  };

  return (
    <div className="lp-wrap" data-testid="verified-page">
      <canvas ref={particlesRef} style={{ position: "fixed", inset: 0, zIndex: 0, width: "100%", height: "100%" }} />

      <div style={{ position: "relative", zIndex: 1, minHeight: "100vh", display: "flex", flexDirection: "column" }}>
        <header style={{ textAlign: "center", padding: "48px 20px 24px", display: "flex", justifyContent: "center" }}>
          <div className="lp-logo-wrapper" style={{ display: "inline-block" }}>
            <img src={LOGO_URL} alt="Agent Eros" style={{ maxWidth: "200px", height: "auto", display: "block" }} />
          </div>
        </header>

        <main style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 20px 80px" }}>
          <div style={{
            maxWidth: "560px",
            width: "100%",
            background: "rgba(17,17,19,0.85)",
            backdropFilter: "blur(20px)",
            border: `1px solid ${isCyber ? "rgba(34,211,238,0.2)" : "rgba(197,165,90,0.25)"}`,
            borderRadius: "16px",
            padding: "48px 40px",
            textAlign: "center",
          }} data-testid="verified-card">

            {/* Success / Already icon */}
            {!isExpired && (
              <div style={{
                width: "72px", height: "72px", borderRadius: "50%",
                background: isAlready
                  ? "rgba(34,211,238,0.12)"
                  : "rgba(16,185,129,0.12)",
                border: `2px solid ${isAlready ? "rgba(34,211,238,0.3)" : "rgba(16,185,129,0.3)"}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                margin: "0 auto 24px",
              }}>
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={isAlready ? "#22d3ee" : "#10b981"} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            )}

            {isExpired ? (
              <>
                <div style={{
                  width: "72px", height: "72px", borderRadius: "50%",
                  background: "rgba(239,68,68,0.12)", border: "2px solid rgba(239,68,68,0.3)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 24px",
                }}>
                  <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <h1 style={{
                  fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                  fontSize: isCyber ? "29px" : "35px", color: "#ef4444", margin: "0 0 12px",
                }}>Link Expired</h1>
                <p style={{ color: "#a1a1aa", fontSize: isCyber ? "18px" : "22px", lineHeight: 1.7, margin: "0 0 32px" }}>
                  This verification link has expired. Please sign up again on our home page to receive a new verification email.
                </p>
              </>
            ) : (
              <>
                <h1 style={{
                  fontFamily: "'Cinzel Decorative', 'Cinzel', serif",
                  fontSize: isCyber ? "29px" : "35px",
                  color: isCyber ? "#22d3ee" : "#C5A55A",
                  margin: "0 0 12px",
                }} data-testid="verified-heading">
                  {isAlready ? "Already Verified" : "Email Verified!"}
                </h1>
                <p style={{ color: "#a1a1aa", fontSize: isCyber ? "18px" : "22px", lineHeight: 1.7, margin: "0 0 12px" }}>
                  {hasAccount
                    ? "Your email is confirmed. Log in with this address to jump straight back into Agent Eros."
                    : "Thanks for confirming your email. Click below to complete your registration — we've pre-filled the form with your verified email so you land straight inside the platform."
                  }
                </p>
                <p style={{ color: "#71717a", fontSize: isCyber ? "16px" : "18px", lineHeight: 1.7, margin: "0 0 32px" }}>
                  Use this verified email address for your Agent Eros account — it's the one now approved on the beta list.
                </p>
              </>
            )}

            {/* Primary CTA — Log In or Complete Registration */}
            {!isExpired && (
              <a
                href={ctaHref}
                onClick={handleCtaClick}
                data-testid="verified-cta-btn"
                data-cta-kind={hasAccount ? "login" : "register"}
                style={{
                  display: "inline-block",
                  padding: "14px 36px",
                  background: isCyber
                    ? "linear-gradient(135deg, #22d3ee, #a855f7)"
                    : "linear-gradient(135deg, #C5A55A, #e8d5a3)",
                  color: "#0a0a0a",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: isCyber ? "18px" : "22px",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  cursor: "pointer",
                  textDecoration: "none",
                  transition: "transform 0.2s, box-shadow 0.2s",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 8px 32px ${isCyber ? "rgba(34,211,238,0.3)" : "rgba(197,165,90,0.3)"}`; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
              >
                {ctaLabel}
              </a>
            )}

            {/* Coin Reward Modal (unchanged) */}
            {rewardState && rewardState.status === "claimed" && (
              <div style={{
                margin: "28px 0 0",
                padding: "20px 24px",
                background: isCyber ? "rgba(16,185,129,0.08)" : "rgba(197,165,90,0.08)",
                border: `1px solid ${isCyber ? "rgba(16,185,129,0.3)" : "rgba(197,165,90,0.3)"}`,
                borderRadius: "12px",
                textAlign: "center",
              }} data-testid="verify-coin-reward">
                <div style={{
                  fontSize: isCyber ? "36px" : "43px",
                  fontWeight: 800,
                  fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                  color: isCyber ? "#10b981" : "#C5A55A",
                  letterSpacing: "1px",
                }}>
                  +{rewardState.coin_amount?.toLocaleString()}
                </div>
                <p style={{ color: "#a1a1aa", fontSize: isCyber ? "15px" : "18px", margin: "4px 0 0" }}>
                  {rewardState.message}
                </p>
                {rewardState.total_coins != null && (
                  <p style={{ color: "#71717a", fontSize: isCyber ? "13px" : "16px", margin: "8px 0 0" }}>
                    Total balance: {rewardState.total_coins?.toLocaleString()} coins
                  </p>
                )}
              </div>
            )}
            {rewardState && rewardState.status === "already_claimed" && (
              <div style={{
                margin: "28px 0 0",
                padding: "16px 24px",
                background: "rgba(34,211,238,0.06)",
                border: "1px solid rgba(34,211,238,0.15)",
                borderRadius: "12px",
                textAlign: "center",
              }} data-testid="verify-coin-already-claimed">
                <p style={{ color: "#a1a1aa", fontSize: isCyber ? "14px" : "17px", margin: 0 }}>
                  Your verification bonus of {rewardState.coin_amount?.toLocaleString()} coins has already been claimed.
                </p>
              </div>
            )}
          </div>
        </main>

        <footer style={{ textAlign: "center", padding: "24px 20px 40px", display: "flex", flexDirection: "column", alignItems: "center" }}>
          <img src={FOOTER_URL} alt="Agent Eros" style={{ maxWidth: "100px", height: "auto", opacity: 0.6, marginBottom: "12px", display: "block" }} />
          <p style={{ color: "#52525b", fontSize: "13px", margin: 0 }}>Agent Eros — Master the Art of Connection</p>
        </footer>
      </div>
    </div>
  );
}
